/* ===== Testimonials Slider: Flickity Init + Controls + Resize Sync (START) ===== */
/*
 * Notes:
 * - Initializes Flickity for testimonial slider sections on load and section re-render.
 * - Connects custom prev/next controls to Flickity navigation with a fallback action.
 * - Syncs layout on resize and destroys listeners/instances when sections unload.
 */
(() => {
	const SECTION_SELECTOR = ".shopify-section.hl-testimonials-slider";

	const initTestimonialsSliderSection = (sectionEl) => {
		if (!sectionEl || !(sectionEl instanceof Element)) return;

		const slider = sectionEl.querySelector(".hl-testimonials-slider-container");
		if (!slider) return;

		const prevControl = sectionEl.querySelector('[data-testimonials-control="prev"]');
		const nextControl = sectionEl.querySelector('[data-testimonials-control="next"]');

		const initSlider = () => {
			if (!window.Flickity) return false;
			if (window.Flickity.data(slider)) return true;

			const flickityInstance = new window.Flickity(slider, {
				cellAlign: "left",
				contain: true,
				draggable: true,
				wrapAround: true,
				pageDots: false,
				prevNextButtons: true,
				setGallerySize: true,
				adaptiveHeight: true,
				resize: true,
				groupCells: false,
			});

			// Ensure initial measurements are correct after layout/styles settle.
			window.requestAnimationFrame(() => {
				flickityInstance.reloadCells();
				flickityInstance.resize();
				flickityInstance.reposition();
			});

			return true;
		};

		const bindControls = () => {
			if (!prevControl || !nextControl) return true;
			if (prevControl.dataset.bound === "true" && nextControl.dataset.bound === "true") return true;

			const flickityInstance = window.Flickity && window.Flickity.data(slider);
			if (!flickityInstance) return false;

			const flickityPrevButton = slider.querySelector(".flickity-prev-next-button.previous");
			const flickityNextButton = slider.querySelector(".flickity-prev-next-button.next");

			const triggerDefaultButton = (buttonEl, fallbackAction) => {
				const previousIndex = flickityInstance.selectedIndex;

				if (buttonEl) {
					buttonEl.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true }));
					if (flickityInstance.selectedIndex !== previousIndex) return;
				}

				fallbackAction();
			};

			prevControl.addEventListener("click", (event) => {
				event.preventDefault();
				triggerDefaultButton(flickityPrevButton, () => flickityInstance.previous(false));
			});

			nextControl.addEventListener("click", (event) => {
				event.preventDefault();
				triggerDefaultButton(flickityNextButton, () => flickityInstance.next(false));
			});

			prevControl.dataset.bound = "true";
			nextControl.dataset.bound = "true";
			return true;
		};

		const bindResizeSync = () => {
			if (slider.dataset.resizeBound === "true") return true;

			const flickityInstance = window.Flickity && window.Flickity.data(slider);
			if (!flickityInstance) return false;

			const syncLayout = () => {
				window.requestAnimationFrame(() => {
					flickityInstance.reloadCells();
					flickityInstance.resize();
					flickityInstance.reposition();
					flickityInstance.select(flickityInstance.selectedIndex, false, true);
				});
			};

			window.addEventListener("resize", syncLayout);
			slider._hlFlickityResizeSync = syncLayout;
			slider.dataset.resizeBound = "true";
			return true;
		};

		let attempts = 0;
		const waitForFlickity = () => {
			if (initSlider() && bindControls() && bindResizeSync()) return;

			attempts += 1;
			if (attempts > 40) return;
			window.setTimeout(waitForFlickity, 50);
		};

		waitForFlickity();
	};

	const initTestimonialsSliders = (root = document) => {
		const sectionRoot = root instanceof Element ? root : document;
		const sections = sectionRoot.matches && sectionRoot.matches(SECTION_SELECTOR) ? [sectionRoot] : sectionRoot.querySelectorAll(SECTION_SELECTOR);

		sections.forEach((sectionEl) => {
			initTestimonialsSliderSection(sectionEl);
		});
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => {
			initTestimonialsSliders();
		});
	} else {
		initTestimonialsSliders();
	}

	document.addEventListener("shopify:section:load", (event) => {
		initTestimonialsSliders(event.target);
	});

	document.addEventListener("shopify:section:unload", (event) => {
		const sectionEl = event.target;
		if (!sectionEl || !(sectionEl instanceof Element) || !sectionEl.matches(SECTION_SELECTOR)) return;

		const slider = sectionEl.querySelector(".hl-testimonials-slider-container");
		if (!slider) return;

		if (slider._hlFlickityResizeSync) {
			window.removeEventListener("resize", slider._hlFlickityResizeSync);
			delete slider._hlFlickityResizeSync;
		}

		const flickityInstance = window.Flickity && window.Flickity.data(slider);
		if (flickityInstance) {
			flickityInstance.destroy();
		}
	});
})();
/* ===== Testimonials Slider: Flickity Init + Controls + Resize Sync (END) ===== */

/* ===== Layout Variable: Announcement Height (START) ===== */
/*
 * Notes:
 * - Exposes --hl-announcement-height on :root for sections that need viewport deductions.
 * - Uses BSTRO announcement bar height when visible; falls back to 0px.
 * - Recalculates on load, resize, and section lifecycle events.
 */
(() => {
	const ROOT_STYLE = document.documentElement && document.documentElement.style;
	if (!ROOT_STYLE) return;

	const ANNOUNCEMENT_SELECTOR = ".bstro-announcement-bar";

	const getAnnouncementHeight = () => {
		const barEl = document.querySelector(ANNOUNCEMENT_SELECTOR);
		if (!barEl) return 0;

		const styles = window.getComputedStyle(barEl);
		if (styles.display === "none" || styles.visibility === "hidden") return 0;

		const rect = barEl.getBoundingClientRect();
		return rect.height > 0 ? Math.round(rect.height) : 0;
	};

	const syncAnnouncementHeightVar = () => {
		ROOT_STYLE.setProperty("--hl-announcement-height", `${getAnnouncementHeight()}px`);
	};

	let resizeTimer = null;
	const handleResize = () => {
		window.clearTimeout(resizeTimer);
		resizeTimer = window.setTimeout(syncAnnouncementHeightVar, 60);
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", syncAnnouncementHeightVar);
	} else {
		syncAnnouncementHeightVar();
	}

	window.addEventListener("load", syncAnnouncementHeightVar);
	window.addEventListener("resize", handleResize);
	document.addEventListener("shopify:section:load", syncAnnouncementHeightVar);
	document.addEventListener("shopify:section:unload", syncAnnouncementHeightVar);
})();
/* ===== Layout Variable: Announcement Height (END) ===== */

/* ===== HL Hero: Embla Init + Cleanup (START) ===== */
/*
 * Notes:
 * - Initializes Embla for HL Hero sections on load and section re-render.
 * - Uses section settings for desktop autoplay and keeps mobile autoplay off.
 * - Destroys listeners/instances when sections unload.
 */
(() => {
	const SECTION_SELECTOR = ".shopify-section.hl-hero";
	const SLIDER_SELECTOR = "[data-hl-hero-slider]";

	const initHeroSliderSection = (sectionEl) => {
		if (!sectionEl || !(sectionEl instanceof Element)) return;

		const slider = sectionEl.querySelector(SLIDER_SELECTOR);
		if (!slider || slider.dataset.emblaInitialized === "true") return;

		const viewport = slider.querySelector("[data-hl-hero-viewport]");
		const slides = Array.from(slider.querySelectorAll("[data-hl-hero-slide]"));
		const prevButton = slider.querySelector('[data-hl-hero-control="prev"]');
		const nextButton = slider.querySelector('[data-hl-hero-control="next"]');

		if (!viewport || !slides.length || typeof window.EmblaCarousel !== "function") return;

		const autoplayEnabled = slider.dataset.autoplay === "true";
		const autoplaySpeed = Number.parseInt(slider.dataset.autoplaySpeed || "7000", 10);
		const autoplayValue = autoplayEnabled ? (Number.isFinite(autoplaySpeed) ? autoplaySpeed : 7000) : false;
		const effectiveAutoplayValue = autoplayValue;
		const disableTouchDrag = window.matchMedia && window.matchMedia("(hover: none) and (pointer: coarse)").matches;
		const useDesktopHeroHeightLock = () => {
			if (!window.matchMedia) return true;
			return window.matchMedia("(min-width: 1024px)").matches;
		};
		const fadePlugins = slider.classList.contains("is-fade") && typeof window.EmblaCarouselFade === "function"
			? [window.EmblaCarouselFade()]
			: [];

		const emblaApi = window.EmblaCarousel(
			viewport,
			{
				align: "start",
				loop: slides.length > 1,
				dragFree: false,
				containScroll: "trimSnaps",
				watchDrag: !disableTouchDrag
			},
			fadePlugins
		);

		slider.dataset.emblaInitialized = "true";

		const syncSelectedSlide = () => {
			const selectedIndex = emblaApi.selectedScrollSnap();
			slides.forEach((slide, index) => {
				const isSelected = index === selectedIndex;
				slide.classList.toggle("is-selected", isSelected);
				if (isSelected) {
					slide.removeAttribute("aria-hidden");
				} else {
					slide.setAttribute("aria-hidden", "true");
				}
			});
		};

		const syncControls = () => {
			if (!prevButton || !nextButton) return;
			const disabled = slides.length <= 1;
			prevButton.disabled = disabled;
			nextButton.disabled = disabled;
		};

		const syncTallestSlideHeight = () => {
			slides.forEach((slide) => {
				slide.style.minHeight = "";
			});
			viewport.style.height = "auto";

			if (!useDesktopHeroHeightLock()) {
				const selectedSlide = slides[emblaApi.selectedScrollSnap()] || slides.find((slide) => slide.classList.contains("is-selected"));
				const selectedHeight = selectedSlide
					? Math.ceil(selectedSlide.scrollHeight || selectedSlide.getBoundingClientRect().height || 0)
					: 0;

				if (selectedHeight > 0) {
					viewport.style.height = `${selectedHeight}px`;
					selectedSlide.style.minHeight = `${selectedHeight}px`;
				}
				return;
			}

			let tallestHeight = 0;
			slides.forEach((slide) => {
				const slideHeight = Math.ceil(slide.scrollHeight || slide.getBoundingClientRect().height || 0);
				if (slideHeight > tallestHeight) tallestHeight = slideHeight;
			});

			if (tallestHeight <= 0) return;

			viewport.style.height = `${tallestHeight}px`;
			slides.forEach((slide) => {
				slide.style.minHeight = `${tallestHeight}px`;
			});
		};

		const scheduleHeightSync = () => {
			window.clearTimeout(slider._hlHeroHeightSyncTimer);
			slider._hlHeroHeightSyncTimer = window.setTimeout(syncTallestSlideHeight, 80);
		};

		let autoplayTimer = null;
		const stopAutoplay = () => {
			window.clearTimeout(autoplayTimer);
			autoplayTimer = null;
		};
		const startAutoplay = () => {
			stopAutoplay();
			if (!effectiveAutoplayValue || slides.length <= 1) return;
			autoplayTimer = window.setTimeout(() => {
				emblaApi.scrollNext();
			}, effectiveAutoplayValue);
		};

		const handleSelect = () => {
			syncSelectedSlide();
			scheduleHeightSync();
			startAutoplay();
		};

		if (prevButton) {
			prevButton.addEventListener("click", () => {
				emblaApi.scrollPrev();
			});
		}
		if (nextButton) {
			nextButton.addEventListener("click", () => {
				emblaApi.scrollNext();
			});
		}
		emblaApi.on("select", handleSelect);
		emblaApi.on("reInit", handleSelect);

		window.addEventListener("resize", scheduleHeightSync);
		window.addEventListener("load", scheduleHeightSync);

		if (document.fonts && document.fonts.ready && typeof document.fonts.ready.then === "function") {
			document.fonts.ready.then(scheduleHeightSync).catch(() => {});
		}

		syncSelectedSlide();
		syncControls();
		window.requestAnimationFrame(scheduleHeightSync);
		window.setTimeout(scheduleHeightSync, 120);
		window.setTimeout(scheduleHeightSync, 500);
		window.requestAnimationFrame(() => {
			window.requestAnimationFrame(() => {
				slider.classList.add("is-animate-ready");
			});
		});
		startAutoplay();

		slider._hlHeroCleanup = () => {
			stopAutoplay();
			window.clearTimeout(slider._hlHeroHeightSyncTimer);
			delete slider._hlHeroHeightSyncTimer;
			window.removeEventListener("resize", scheduleHeightSync);
			window.removeEventListener("load", scheduleHeightSync);
			emblaApi.destroy();
			delete slider.dataset.emblaInitialized;
			delete slider._hlHeroCleanup;
		};
	};

	const initHeroSliders = (root = document) => {
		const sectionRoot = root instanceof Element ? root : document;
		const sections = sectionRoot.matches && sectionRoot.matches(SECTION_SELECTOR) ? [sectionRoot] : sectionRoot.querySelectorAll(SECTION_SELECTOR);

		sections.forEach((sectionEl) => {
			initHeroSliderSection(sectionEl);
		});
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => {
			initHeroSliders();
		});
	} else {
		initHeroSliders();
	}

	document.addEventListener("shopify:section:load", (event) => {
		initHeroSliders(event.target);
	});

	document.addEventListener("shopify:section:unload", (event) => {
		const sectionEl = event.target;
		if (!sectionEl || !(sectionEl instanceof Element) || !sectionEl.matches(SECTION_SELECTOR)) return;

		const slider = sectionEl.querySelector(SLIDER_SELECTOR);
		if (!slider) return;

		if (typeof slider._hlHeroCleanup === "function") {
			slider._hlHeroCleanup();
		}
	});
})();
/* ===== HL Hero: Embla Init + Cleanup (END) ===== */

/* Recharge widget customizations moved to assets/bstro-recharge-archive.js (not loaded). */

/* ===== Unique Slider: Flickity + Hover/Floating Captions + Cleanup (START) ===== */
/*
 * Notes:
 * - Initializes the unique slider with Flickity and retries until dependency is ready.
 * - Adds desktop hover-follow captions, including optional overflow/floating mode.
 * - Repositions controls, syncs layout on resize, and removes handlers on unload.
 */
(() => {
	const SECTION_SELECTOR = ".shopify-section.hl-unique-slider";
	const SLIDER_SELECTOR = "[data-unique-slider]";
	const SLIDE_SELECTOR = "[data-unique-slide]";
	const CAPTION_SELECTOR = "[data-unique-caption]";
	const PAGE_WIDTH_SELECTOR = ".page-width";
	const CONTROL_SELECTOR = ".flickity-prev-next-button";

	const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
	const canUseHoverCaptions = () => {
		if (!window.matchMedia) return false;
		return window.matchMedia("(min-width: 1024px) and (hover: hover) and (pointer: fine)").matches;
	};
	const canShowInitialFloatingCaption = () => {
		if (!window.matchMedia) return false;
		return window.matchMedia("(min-width: 1280px)").matches;
	};
	const isMediumDesktopCaptionRange = () => {
		if (!window.matchMedia) return false;
		return window.matchMedia("(min-width: 1025px) and (max-width: 1279px)").matches;
	};

	const ensureFloatingCaption = (sliderEl) => {
		if (!sliderEl || !(sliderEl instanceof Element)) return null;
		if (sliderEl._hlUniqueFloatingCaption) return sliderEl._hlUniqueFloatingCaption;

		const floatingCaptionEl = document.createElement("div");
		floatingCaptionEl.className = "hl-unique-slider-caption glassify blue";
		floatingCaptionEl.setAttribute("data-unique-floating-caption", "");
		sliderEl.appendChild(floatingCaptionEl);
		sliderEl._hlUniqueFloatingCaption = floatingCaptionEl;
		return floatingCaptionEl;
	};

	const cleanupFloatingCaption = (sliderEl) => {
		if (sliderEl && sliderEl._hlUniqueInitialCaptionRaf) {
			window.cancelAnimationFrame(sliderEl._hlUniqueInitialCaptionRaf);
			delete sliderEl._hlUniqueInitialCaptionRaf;
		}
		if (sliderEl && sliderEl._hlUniqueInitialCaptionTimeout) {
			window.clearTimeout(sliderEl._hlUniqueInitialCaptionTimeout);
			delete sliderEl._hlUniqueInitialCaptionTimeout;
		}
		if (!sliderEl || !sliderEl._hlUniqueFloatingCaption) return;
		sliderEl._hlUniqueFloatingCaption.remove();
		delete sliderEl._hlUniqueFloatingCaption;
	};

	const hideDefaultFloatingCaption = (sliderEl, markInteracted = true) => {
		if (!sliderEl) return;
		const floatingCaptionEl = sliderEl._hlUniqueFloatingCaption;
		if (!floatingCaptionEl || !floatingCaptionEl.classList.contains("is-default")) return;

		if (sliderEl._hlUniqueInitialCaptionRaf) {
			window.cancelAnimationFrame(sliderEl._hlUniqueInitialCaptionRaf);
			delete sliderEl._hlUniqueInitialCaptionRaf;
		}
		if (sliderEl._hlUniqueInitialCaptionTimeout) {
			window.clearTimeout(sliderEl._hlUniqueInitialCaptionTimeout);
			delete sliderEl._hlUniqueInitialCaptionTimeout;
		}

		floatingCaptionEl.classList.remove("is-active", "is-default");
		floatingCaptionEl.innerHTML = "";
		if (markInteracted) {
			sliderEl._hlUniqueCaptionInteracted = true;
		}
	};

	const showInitialFloatingCaption = (sliderEl, slides) => {
		if (!sliderEl || sliderEl._hlUniqueCaptionInteracted) return;
		if (!slides || !slides.length) return;

		let firstCaptionSource = null;
		for (const slideEl of slides) {
			firstCaptionSource = slideEl.querySelector(CAPTION_SELECTOR);
			if (firstCaptionSource) break;
		}
		if (!firstCaptionSource) return;

		const floatingCaptionEl = ensureFloatingCaption(sliderEl);
		if (!floatingCaptionEl) return;
		if (floatingCaptionEl.classList.contains("is-default") && floatingCaptionEl.classList.contains("is-active")) return;

		if (sliderEl._hlUniqueInitialCaptionRaf) {
			window.cancelAnimationFrame(sliderEl._hlUniqueInitialCaptionRaf);
			delete sliderEl._hlUniqueInitialCaptionRaf;
		}
		if (sliderEl._hlUniqueInitialCaptionTimeout) {
			window.clearTimeout(sliderEl._hlUniqueInitialCaptionTimeout);
			delete sliderEl._hlUniqueInitialCaptionTimeout;
		}

		floatingCaptionEl.innerHTML = firstCaptionSource.innerHTML;
		floatingCaptionEl.classList.remove("is-active");
		floatingCaptionEl.classList.add("is-default");
		floatingCaptionEl.style.transform = "translate3d(-55%, -45%, 0)";
		sliderEl._hlUniqueInitialCaptionRaf = window.requestAnimationFrame(() => {
			sliderEl._hlUniqueInitialCaptionTimeout = window.setTimeout(() => {
				floatingCaptionEl.classList.add("is-active");
				delete sliderEl._hlUniqueInitialCaptionTimeout;
			}, 800);
			delete sliderEl._hlUniqueInitialCaptionRaf;
		});
	};

	const bindCaptionFollower = (slideEl, sliderEl, allowOverflow = false) => {
		if (!slideEl || slideEl.dataset.captionBound === "true") return;

		const sourceCaptionEl = slideEl.querySelector(CAPTION_SELECTOR);
		if (!sourceCaptionEl) return;

		let rafId = 0;
		let pointerX = 20;
		let pointerY = 20;
		let pointerClientX = 20;
		let pointerClientY = 20;
		const cursorOffset = 16;

		const render = () => {
			rafId = 0;

			if (allowOverflow) {
				const floatingCaptionEl = sliderEl && sliderEl._hlUniqueFloatingCaption;
				if (!floatingCaptionEl || !floatingCaptionEl.classList.contains("is-active")) return;
				if (floatingCaptionEl.classList.contains("is-default")) return;

				const sliderRect = sliderEl.getBoundingClientRect();
				if (!sliderRect.width || !sliderRect.height) return;
				const captionRect = floatingCaptionEl.getBoundingClientRect();
				const slideRect = slideEl.getBoundingClientRect();
				const useViewportPositioning = isMediumDesktopCaptionRange();
				let targetX = 0;
				let targetY = 0;

				if (useViewportPositioning) {
					const viewportWidth = window.innerWidth || document.documentElement.clientWidth || sliderRect.width;
					const viewportHeight = window.innerHeight || document.documentElement.clientHeight || sliderRect.height;
					const viewportPadding = 12;
					const slideCenterX = slideRect.left + slideRect.width / 2;
					const shouldOpenRight = slideCenterX < viewportWidth / 2;
					const baseClientX = shouldOpenRight ? pointerClientX + cursorOffset : pointerClientX - captionRect.width - cursorOffset;
					const baseClientY = pointerClientY - captionRect.height - cursorOffset;
					const maxClientX = Math.max(viewportPadding, viewportWidth - captionRect.width - viewportPadding);
					const maxClientY = Math.max(viewportPadding, viewportHeight - captionRect.height - viewportPadding);
					const clampedClientX = clamp(baseClientX, viewportPadding, maxClientX);
					const clampedClientY = clamp(baseClientY, viewportPadding, maxClientY);

					targetX = clampedClientX - sliderRect.left;
					targetY = clampedClientY - sliderRect.top;
				} else {
					const baseTargetX = pointerX - captionRect.width - cursorOffset;
					const baseTargetY = pointerY - captionRect.height - cursorOffset;
					targetX = baseTargetX;
					targetY = baseTargetY;
				}

				floatingCaptionEl.style.transform = `translate3d(${targetX}px, ${targetY}px, 0)`;
				return;
			}

			const slideRect = slideEl.getBoundingClientRect();
			if (!slideRect.width || !slideRect.height) return;

			const captionRect = sourceCaptionEl.getBoundingClientRect();
			const maxX = Math.max(0, slideRect.width - captionRect.width);
			const maxY = Math.max(0, slideRect.height - captionRect.height);
			const targetX = pointerX - captionRect.width - cursorOffset;
			const targetY = pointerY - captionRect.height - cursorOffset;
			const nextX = clamp(targetX, 0, maxX);
			const nextY = clamp(targetY, 0, maxY);

			sourceCaptionEl.style.transform = `translate3d(${nextX}px, ${nextY}px, 0)`;
		};

		const queueRender = () => {
			if (rafId) return;
			rafId = window.requestAnimationFrame(render);
		};

		const handleEnter = (event) => {
			if (allowOverflow) {
				const floatingCaptionEl = ensureFloatingCaption(sliderEl);
				if (floatingCaptionEl) {
					sliderEl._hlUniqueCaptionInteracted = true;
					floatingCaptionEl.innerHTML = sourceCaptionEl.innerHTML;
					floatingCaptionEl.classList.remove("is-default");
					floatingCaptionEl.classList.add("is-active");
				}
			} else {
				slideEl.classList.add("is-caption-active");
			}

			handleMove(event);
		};

		const handleMove = (event) => {
			if (allowOverflow) {
				const sliderRect = sliderEl.getBoundingClientRect();
				pointerX = event.clientX - sliderRect.left;
				pointerY = event.clientY - sliderRect.top;
				pointerClientX = event.clientX;
				pointerClientY = event.clientY;
			} else {
				const slideRect = slideEl.getBoundingClientRect();
				pointerX = event.clientX - slideRect.left;
				pointerY = event.clientY - slideRect.top;
			}

			queueRender();
		};

		const handleLeave = () => {
			if (allowOverflow) {
				const floatingCaptionEl = sliderEl && sliderEl._hlUniqueFloatingCaption;
				if (floatingCaptionEl) {
					floatingCaptionEl.classList.remove("is-active", "is-default");
					floatingCaptionEl.innerHTML = "";
				}
			} else {
				slideEl.classList.remove("is-caption-active");
			}

			pointerX = 20;
			pointerY = 20;
			pointerClientX = 20;
			pointerClientY = 20;
			queueRender();
		};

		slideEl.addEventListener("mouseenter", handleEnter);
		slideEl.addEventListener("mousemove", handleMove);
		slideEl.addEventListener("mouseleave", handleLeave);

		slideEl._hlUniqueCaptionCleanup = () => {
			slideEl.removeEventListener("mouseenter", handleEnter);
			slideEl.removeEventListener("mousemove", handleMove);
			slideEl.removeEventListener("mouseleave", handleLeave);
			slideEl.classList.remove("is-caption-active");

			if (allowOverflow) {
				const floatingCaptionEl = sliderEl && sliderEl._hlUniqueFloatingCaption;
				if (floatingCaptionEl) {
					floatingCaptionEl.classList.remove("is-active", "is-default");
					floatingCaptionEl.innerHTML = "";
				}
			}

			if (rafId) {
				window.cancelAnimationFrame(rafId);
				rafId = 0;
			}

			delete slideEl.dataset.captionBound;
			sourceCaptionEl.style.transform = "";
			delete slideEl._hlUniqueCaptionCleanup;
		};

		slideEl.dataset.captionBound = "true";
		queueRender();
	};

	const cleanupCaptionFollowers = (sectionEl) => {
		const slides = sectionEl.querySelectorAll(SLIDE_SELECTOR);
		slides.forEach((slideEl) => {
			if (slideEl._hlUniqueCaptionCleanup) {
				slideEl._hlUniqueCaptionCleanup();
			}
		});

		const slider = sectionEl.querySelector(SLIDER_SELECTOR);
		if (slider && slider._hlUniqueDefaultCaptionDismissCleanup) {
			slider._hlUniqueDefaultCaptionDismissCleanup();
		}
		cleanupFloatingCaption(slider);
	};

	const initUniqueSliderSection = (sectionEl) => {
		if (!sectionEl || !(sectionEl instanceof Element)) return;

		const slider = sectionEl.querySelector(SLIDER_SELECTOR);
		if (!slider) return;
		const pageWidthEl = sectionEl.querySelector(PAGE_WIDTH_SELECTOR);

		const initSlider = () => {
			if (!window.Flickity) return false;
			if (window.Flickity.data(slider)) return true;

			const slideCount = slider.querySelectorAll(SLIDE_SELECTOR).length;
			const flickityInstance = new window.Flickity(slider, {
				cellSelector: SLIDE_SELECTOR,
				cellAlign: "left",
				contain: true,
				draggable: true,
				wrapAround: true,
				pageDots: false,
				prevNextButtons: true,
				setGallerySize: true,
				adaptiveHeight: true,
				resize: true,
				imagesLoaded: true,
				groupCells: true,
			});

			window.requestAnimationFrame(() => {
				flickityInstance.reloadCells();
				flickityInstance.resize();
				flickityInstance.reposition();
			});

			return true;
		};

		const bindResizeSync = () => {
			if (slider.dataset.resizeBound === "true") return true;

			const flickityInstance = window.Flickity && window.Flickity.data(slider);
			if (!flickityInstance) return false;

			const syncLayout = () => {
				window.requestAnimationFrame(() => {
					flickityInstance.reloadCells();
					flickityInstance.resize();
					flickityInstance.reposition();
					bindCaptionFollowers();
				});
			};

			window.addEventListener("resize", syncLayout);
			slider._hlUniqueResizeSync = syncLayout;
			slider.dataset.resizeBound = "true";
			return true;
		};

		const movePrevNextButtonsToPageWidth = () => {
			if (!pageWidthEl) return true;

			const prevNextButtons = slider.querySelectorAll(".flickity-prev-next-button");
			if (!prevNextButtons.length) return false;

			prevNextButtons.forEach((buttonEl) => {
				if (buttonEl.parentElement !== pageWidthEl) {
					pageWidthEl.appendChild(buttonEl);
				}
			});

			return true;
		};

		const bindDefaultCaptionDismiss = () => {
			if (!pageWidthEl) return true;
			if (slider.dataset.defaultCaptionDismissBound === "true") return true;

			const handleMouseOver = (event) => {
				const target = event.target;
				if (!(target instanceof Element)) return;
				if (slider.dataset.captionOverflow !== "true") return;
				if (!target.closest(SLIDER_SELECTOR) && !target.closest(CONTROL_SELECTOR)) return;
				hideDefaultFloatingCaption(slider, true);
			};

			pageWidthEl.addEventListener("mouseover", handleMouseOver);
			slider._hlUniqueDefaultCaptionDismissCleanup = () => {
				pageWidthEl.removeEventListener("mouseover", handleMouseOver);
				delete slider._hlUniqueDefaultCaptionDismissCleanup;
				delete slider.dataset.defaultCaptionDismissBound;
			};
			slider.dataset.defaultCaptionDismissBound = "true";
			return true;
		};

		const bindCaptionFollowers = () => {
			if (!canUseHoverCaptions()) {
				cleanupCaptionFollowers(sectionEl);
				return true;
			}

			const allowOverflow = slider.dataset.captionOverflow === "true";
			const slides = slider.querySelectorAll(SLIDE_SELECTOR);
			slides.forEach((slideEl) => {
				bindCaptionFollower(slideEl, slider, allowOverflow);
			});

			if (allowOverflow) {
				if (canShowInitialFloatingCaption()) {
					showInitialFloatingCaption(slider, slides);
				} else {
					hideDefaultFloatingCaption(slider, false);
				}
			}
			return true;
		};

		let attempts = 0;
		const waitForFlickity = () => {
			if (initSlider() && movePrevNextButtonsToPageWidth() && bindDefaultCaptionDismiss() && bindResizeSync() && bindCaptionFollowers()) return;

			attempts += 1;
			if (attempts > 40) return;
			window.setTimeout(waitForFlickity, 50);
		};

		waitForFlickity();
	};

	const initUniqueSliders = (root = document) => {
		const sectionRoot = root instanceof Element ? root : document;
		const sections = sectionRoot.matches && sectionRoot.matches(SECTION_SELECTOR) ? [sectionRoot] : sectionRoot.querySelectorAll(SECTION_SELECTOR);

		sections.forEach((sectionEl) => {
			initUniqueSliderSection(sectionEl);
		});
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => {
			initUniqueSliders();
		});
	} else {
		initUniqueSliders();
	}

	document.addEventListener("shopify:section:load", (event) => {
		initUniqueSliders(event.target);
	});

	document.addEventListener("shopify:section:unload", (event) => {
		const sectionEl = event.target;
		if (!sectionEl || !(sectionEl instanceof Element) || !sectionEl.matches(SECTION_SELECTOR)) return;

		cleanupCaptionFollowers(sectionEl);

		const slider = sectionEl.querySelector(SLIDER_SELECTOR);
		if (!slider) return;

		if (slider._hlUniqueResizeSync) {
			window.removeEventListener("resize", slider._hlUniqueResizeSync);
			delete slider._hlUniqueResizeSync;
		}

		const flickityInstance = window.Flickity && window.Flickity.data(slider);
		if (flickityInstance) {
			flickityInstance.destroy();
		}
	});
})();
/* ===== Unique Slider: Flickity + Hover/Floating Captions + Cleanup (END) ===== */

/* ===== FAQ Deep Links: Hash -> Open Row + Sticky Offset Scroll (START) ===== */
/*
 * Notes:
 * - Reads URL hash values and maps them to matching FAQ triggers/panels.
 * - Opens the target FAQ item when needed and scrolls with sticky header offset.
 * - Responds to both initial page load and later hash changes.
 */
(() => {
	const FAQ_TRIGGER_SELECTOR = ".hl-faq .hl-faq-trigger";

	const normalizeHash = (value) => {
		const rawValue = (value || "").replace(/^#/, "").trim();
		try {
			return decodeURIComponent(rawValue).toLowerCase();
		} catch (error) {
			return rawValue.toLowerCase();
		}
	};

	const getElementHeight = (selector) => {
		const el = document.querySelector(selector);
		if (!el) return 0;

		const styles = window.getComputedStyle(el);
		if (styles.display === "none" || styles.visibility === "hidden") return 0;

		const rect = el.getBoundingClientRect();
		return rect.height > 0 ? rect.height : 0;
	};

	const getStickyHeaderOffset = () => {
		const siteHeaderHeight = Math.max(
			getElementHeight(".site-header.site-header--stuck"),
			getElementHeight(".header-wrapper--sticky .site-header"),
			getElementHeight(".header-sticky-wrapper .site-header"),
		);
		const toolbarHeight = getElementHeight(".toolbar");

		// Keep a little breathing room between menu and opened FAQ row.
		return Math.round(siteHeaderHeight + toolbarHeight + 12);
	};

	const scrollToTrigger = (triggerEl) => {
		if (!triggerEl) return;

		const offset = getStickyHeaderOffset();
		const targetTop = window.pageYOffset + triggerEl.getBoundingClientRect().top - offset;
		window.scrollTo({ top: Math.max(targetTop, 0), behavior: "smooth" });
	};

	const findTriggerByPanelId = (panelId) => {
		if (!panelId) return null;
		return document.querySelector(`${FAQ_TRIGGER_SELECTOR}[aria-controls="${panelId}"]`);
	};

	const findTriggerByAnchor = (rawHash) => {
		const normalizedHash = normalizeHash(rawHash);
		if (!normalizedHash) return null;

		const triggers = document.querySelectorAll(FAQ_TRIGGER_SELECTOR);
		for (const trigger of triggers) {
			const triggerAnchor = normalizeHash(trigger.getAttribute("data-faq-anchor"));
			if (!triggerAnchor) continue;
			if (triggerAnchor === normalizedHash) return trigger;
			if (`faq-${triggerAnchor}` === normalizedHash) return trigger;
		}

		return null;
	};

	const openFaqFromHash = () => {
		const rawHash = window.location.hash;
		const normalizedHash = normalizeHash(rawHash);
		if (!normalizedHash) return;

		let trigger = null;
		const directTarget = document.getElementById(normalizedHash);

		if (directTarget) {
			if (directTarget.matches(FAQ_TRIGGER_SELECTOR)) {
				trigger = directTarget;
			} else if (directTarget.matches(".collapsible-content")) {
				trigger = findTriggerByPanelId(directTarget.id);
			} else {
				trigger = directTarget.querySelector(FAQ_TRIGGER_SELECTOR);
			}
		}

		if (!trigger) {
			trigger = findTriggerByAnchor(normalizedHash);
		}

		if (!trigger) return;

		const isOpen = trigger.classList.contains("is-open");
		if (!isOpen) {
			trigger.click();
		}

		window.setTimeout(
			() => {
				scrollToTrigger(trigger);
			},
			isOpen ? 0 : 250,
		);
	};

	const initFaqDeepLinks = () => {
		openFaqFromHash();
		window.addEventListener("hashchange", openFaqFromHash);
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", initFaqDeepLinks);
	} else {
		initFaqDeepLinks();
	}
})();
/* ===== FAQ Deep Links: Hash -> Open Row + Sticky Offset Scroll (END) ===== */

/* ===== Zendesk Trigger: Click Handler To Open Widget (START) ===== */
/*
 * Notes:
 * - Listens for clicks on elements marked with [data-hl-zendesk-open].
 * - Prevents default navigation behavior and opens the Zendesk Web Widget.
 * - Fails safely when the Zendesk API is not available.
 */
(() => {
	const ZENDESK_TRIGGER_SELECTOR = "[data-hl-zendesk-open]";

	const openZendeskWidget = () => {
		if (typeof window.zE !== "function") return;

		try {
			window.zE("webWidget", "open");
		} catch (error) {
			// no-op
		}
	};

	document.addEventListener("click", (event) => {
		const trigger = event.target.closest(ZENDESK_TRIGGER_SELECTOR);
		if (!trigger) return;

		event.preventDefault();
		openZendeskWidget();
	});
})();
/* ===== Zendesk Trigger: Click Handler To Open Widget (END) ===== */

/* ===== PDP Review Summary: Judge.me Hover Panel + Scroll Link (START) ===== */
/*
 * Notes:
 * - Hydrates the custom PDP review summary from the rendered Judge.me review widget.
 * - Supports hover, focus, and click interactions for the histogram panel.
 * - Scrolls to the large review widget when the CTA is clicked.
 */
(() => {
	const SUMMARY_SELECTOR = "[data-hl-review-summary]";
	const HOVER_MEDIA_QUERY = "(hover: hover) and (pointer: fine)";

	const parseNumber = (value) => {
		if (value === null || value === undefined) return null;
		const normalized = String(value).replace(/,/g, "").match(/-?\d+(\.\d+)?/);
		return normalized ? Number.parseFloat(normalized[0]) : null;
	};

	const formatAverage = (value) => {
		if (!Number.isFinite(value)) return "0.0";
		return value.toFixed(value >= 4 ? 2 : 1);
	};

	const formatCount = (value) => {
		if (!Number.isFinite(value)) return "0";
		return new Intl.NumberFormat("en-US").format(value);
	};

	const getHoverEnabled = () => {
		if (!window.matchMedia) return false;
		return window.matchMedia(HOVER_MEDIA_QUERY).matches;
	};

	const getReviewWidget = (productId) => {
		const widgets = Array.from(document.querySelectorAll("#judgeme_product_reviews, .jdgm-widget.jdgm-review-widget"));
		return widgets.find((widgetEl) => {
			const widgetProductId = widgetEl.getAttribute("data-product-id") || widgetEl.getAttribute("data-id");
			return !productId || String(widgetProductId) === String(productId);
		}) || null;
	};

	const parseEmbeddedWidgetData = (summaryEl) => {
		if (!summaryEl) return null;
		const dataEl = summaryEl.querySelector("[data-hl-review-widget-data]");
		if (!dataEl) return null;

		try {
			return JSON.parse(dataEl.textContent);
		} catch (error) {
			return null;
		}
	};

	const getGlobalWidgetData = (productId) => {
		if (!window.jdgm || !window.jdgm.data || !window.jdgm.data.reviewWidget || !productId) return null;
		return window.jdgm.data.reviewWidget[productId] || window.jdgm.data.reviewWidget[String(productId)] || null;
	};

	const collectNumericValues = (value, bucket = [], seen = new WeakSet()) => {
		if (value === null || value === undefined) return bucket;

		if (typeof value === "number" && Number.isFinite(value)) {
			bucket.push(value);
			return bucket;
		}

		if (typeof value === "string") {
			const parsed = parseNumber(value);
			if (Number.isFinite(parsed)) {
				bucket.push(parsed);
			}
			return bucket;
		}

		if (typeof value !== "object") return bucket;
		if (seen.has(value)) return bucket;
		seen.add(value);

		if (Array.isArray(value)) {
			value.forEach((entry) => collectNumericValues(entry, bucket, seen));
			return bucket;
		}

		Object.values(value).forEach((entry) => collectNumericValues(entry, bucket, seen));
		return bucket;
	};

	const findValueByKey = (source, matcher, seen = new WeakSet()) => {
		if (!source || typeof source !== "object") return null;
		if (seen.has(source)) return null;
		seen.add(source);

		if (Array.isArray(source)) {
			for (const entry of source) {
				const match = findValueByKey(entry, matcher, seen);
				if (match !== null && match !== undefined) return match;
			}
			return null;
		}

		for (const [key, value] of Object.entries(source)) {
			if (matcher(key, value)) return value;
			if (value && typeof value === "object") {
				const match = findValueByKey(value, matcher, seen);
				if (match !== null && match !== undefined) return match;
			}
		}

		return null;
	};

	const getAverageFromData = (widgetData) => {
		if (!widgetData || typeof widgetData !== "object") return null;

		const direct = findValueByKey(widgetData, (key, value) => {
			if (!/average|rating/i.test(key)) return false;
			return Number.isFinite(parseNumber(value));
		});

		const parsedDirect = parseNumber(direct);
		if (Number.isFinite(parsedDirect)) return parsedDirect;

		return null;
	};

	const getReviewCountFromData = (widgetData) => {
		if (!widgetData || typeof widgetData !== "object") return null;

		const direct = findValueByKey(widgetData, (key, value) => {
			if (!/review.*count|count.*review|reviews_count|number.*review/i.test(key)) return false;
			return Number.isFinite(parseNumber(value));
		});

		const parsedDirect = parseNumber(direct);
		if (Number.isFinite(parsedDirect)) return parsedDirect;

		return null;
	};

	const getHistogramFromData = (widgetData) => {
		if (!widgetData || typeof widgetData !== "object") return [];

		const histogramValue = findValueByKey(widgetData, (key, value) => {
			return /histogram|distribution|rating.*count|count.*rating|star.*count/i.test(key) && value && typeof value === "object";
		});

		if (!histogramValue) return [];

		const rows = [];

		if (Array.isArray(histogramValue)) {
			histogramValue.forEach((entry) => {
				if (!entry || typeof entry !== "object") return;
				const star = parseNumber(entry.star || entry.rating || entry.label || entry.name);
				const count = parseNumber(entry.count || entry.total || entry.value || entry.frequency);
				if (Number.isFinite(star) && Number.isFinite(count)) {
					rows.push({ star: Math.round(star), count });
				}
			});
		} else {
			Object.entries(histogramValue).forEach(([key, value]) => {
				const star = parseNumber(key);
				if (!Number.isFinite(star)) return;
				const numericValues = collectNumericValues(value, []);
				const count = numericValues.length ? numericValues[numericValues.length - 1] : parseNumber(value);
				if (Number.isFinite(count)) {
					rows.push({ star: Math.round(star), count });
				}
			});
		}

		return rows.filter((row) => row.star >= 1 && row.star <= 5);
	};

	const getAverageFromWidget = (widgetEl) => {
		if (!widgetEl) return null;

		const directAverage = parseNumber(widgetEl.getAttribute("data-average-rating"));
		if (Number.isFinite(directAverage)) return directAverage;

		const candidates = [
			".jdgm-rev-widg__summary-average",
			".jdgm-prev-badge__stars + .jdgm-prev-badge__text",
			".jdgm-star-rating",
			".jdgm-widget-actions-wrapper ~ .jdgm-rev-widg__summary-average",
		];

		for (const selector of candidates) {
			const node = widgetEl.querySelector(selector);
			const value = parseNumber(node && node.textContent);
			if (Number.isFinite(value)) return value;
		}

		return null;
	};

	const getReviewCountFromWidget = (widgetEl) => {
		if (!widgetEl) return null;

		const directCount = parseNumber(
			widgetEl.getAttribute("data-number-of-reviews")
			|| widgetEl.getAttribute("data-review-count")
			|| widgetEl.getAttribute("data-reviews-count")
		);
		if (Number.isFinite(directCount)) return directCount;

		const candidates = [
			".jdgm-rev-widg__summary-text",
			".jdgm-prev-badge__text",
			".jdgm-histogram__totals",
			".jdgm-rev-widg__reviews-count",
		];

		for (const selector of candidates) {
			const node = widgetEl.querySelector(selector);
			const text = node && node.textContent;
			if (!text) continue;
			const matches = text.match(/[\d,]+/g);
			if (!matches || !matches.length) continue;
			const value = parseNumber(matches[matches.length - 1]);
			if (Number.isFinite(value)) return value;
		}

		return null;
	};

	const getHistogramFromWidget = (widgetEl) => {
		if (!widgetEl) return [];

		const rows = Array.from(widgetEl.querySelectorAll(".jdgm-histogram__row"));
		if (!rows.length) return [];

		return rows.map((rowEl) => {
			const starSource = rowEl.querySelector(".jdgm-histogram__star, .jdgm-histogram__label");
			const countSource = rowEl.querySelector(".jdgm-histogram__frequency, .jdgm-histogram__count");
			const rowText = rowEl.textContent || "";
			const textMatches = rowText.match(/[\d,]+/g) || [];
			const star = parseNumber(starSource && starSource.textContent) || parseNumber(textMatches[0]);
			const count = parseNumber(countSource && countSource.textContent) || parseNumber(textMatches[textMatches.length - 1]) || 0;

			return {
				star: Number.isFinite(star) ? Math.round(star) : null,
				count: Number.isFinite(count) ? count : 0,
			};
		}).filter((row) => Number.isFinite(row.star) && row.star >= 1 && row.star <= 5);
	};

	const updateSummary = (summaryEl) => {
		if (!summaryEl || !(summaryEl instanceof Element)) return false;

		const productId = summaryEl.dataset.productId;
		const embeddedWidgetData = parseEmbeddedWidgetData(summaryEl);
		const globalWidgetData = getGlobalWidgetData(productId);
		const widgetData = embeddedWidgetData || globalWidgetData;
		const widgetEl = getReviewWidget(productId);

		const average = getAverageFromData(widgetData) ?? getAverageFromWidget(widgetEl);
		const histogram = getHistogramFromData(widgetData).length ? getHistogramFromData(widgetData) : getHistogramFromWidget(widgetEl);
		const histogramTotal = histogram.reduce((sum, row) => sum + row.count, 0);
		const reviewCount = getReviewCountFromData(widgetData) || getReviewCountFromWidget(widgetEl) || histogramTotal || 0;
		const normalizedAverage = Number.isFinite(average)
			? average
			: (histogramTotal
				? histogram.reduce((sum, row) => sum + row.star * row.count, 0) / histogramTotal
				: 0);
		const hideWithoutReviews = summaryEl.dataset.hideWithoutReviews === "true";
		const hasJudgeMeReviews = reviewCount > 0;
		const blockEl = summaryEl.closest(".product-block--hl-review-summary") || summaryEl;

		const ratingEl = summaryEl.querySelector("[data-hl-review-rating]");
		const countEl = summaryEl.querySelector("[data-hl-review-count]");
		const starEls = summaryEl.querySelectorAll("[data-hl-review-star]");
		const ctaEl = summaryEl.querySelector("[data-hl-review-scroll]");

		blockEl.hidden = hideWithoutReviews && !hasJudgeMeReviews;
		if (ratingEl) ratingEl.textContent = formatAverage(normalizedAverage);
		if (countEl) countEl.textContent = `(${formatCount(reviewCount)} review${reviewCount === 1 ? "" : "s"})`;
		if (starEls.length) {
			const clampedAverage = Math.max(0, Math.min(5, normalizedAverage));
			starEls.forEach((starEl, index) => {
				const fillEl = starEl.querySelector("[data-hl-review-star-fill]");
				const clipEl = starEl.querySelector("[data-hl-review-star-clip]");
				const starProgress = Math.max(0, Math.min(1, clampedAverage - index));
				starEl.classList.toggle("is-full", starProgress >= 0.999);
				starEl.classList.toggle("is-empty", starProgress <= 0.001);
				starEl.classList.toggle("is-partial", starProgress > 0.001 && starProgress < 0.999);
				if (fillEl) {
					fillEl.style.width = "100%";
				}
				if (clipEl) {
					clipEl.setAttribute("width", String(24 * starProgress));
				}
			});
		}
		if (ctaEl) ctaEl.textContent = `Read ${formatCount(reviewCount)} Review${reviewCount === 1 ? "" : "s"}`;

		const rows = summaryEl.querySelectorAll("[data-hl-review-row]");
		if (rows.length) {
			const countsByStar = new Map(histogram.map((row) => [String(row.star), row.count]));
			rows.forEach((rowEl) => {
				const star = rowEl.getAttribute("data-hl-review-row");
				const count = countsByStar.get(String(star)) || 0;
				const width = reviewCount > 0 ? (count / reviewCount) * 100 : 0;
				const fillEl = rowEl.querySelector("[data-hl-review-bar-fill]");
				const countNode = rowEl.querySelector("[data-hl-review-row-count]");

				if (fillEl) fillEl.style.width = `${width}%`;
				if (countNode) countNode.textContent = formatCount(count);
			});
		}

		return !!widgetData || Number.isFinite(average) || reviewCount > 0 || histogram.length > 0;
	};

	const scrollToReviewWidget = (summaryEl) => {
		const widgetEl = getReviewWidget(summaryEl && summaryEl.dataset.productId);
		if (!widgetEl) {
			// Overridden product whose reviews aren't on this page: go to its PDP instead.
			const productUrl = summaryEl && summaryEl.dataset.productUrl;
			if (productUrl && productUrl !== window.location.pathname) {
				window.location.href = `${productUrl}#judgeme_product_reviews`;
			}
			return;
		}

		const headerOffset = 120;
		const top = widgetEl.getBoundingClientRect().top + window.scrollY - headerOffset;
		window.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
	};

	const closeSummary = (summaryEl) => {
		if (!summaryEl) return;
		summaryEl.classList.remove("is-open");
		const triggerEl = summaryEl.querySelector("[data-hl-review-trigger]");
		const panelEl = summaryEl.querySelector("[data-hl-review-panel]");
		if (triggerEl) triggerEl.setAttribute("aria-expanded", "false");
		if (panelEl) panelEl.hidden = true;
	};

	const openSummary = (summaryEl) => {
		if (!summaryEl) return;
		summaryEl.classList.add("is-open");
		const triggerEl = summaryEl.querySelector("[data-hl-review-trigger]");
		const panelEl = summaryEl.querySelector("[data-hl-review-panel]");
		if (triggerEl) triggerEl.setAttribute("aria-expanded", "true");
		if (panelEl) panelEl.hidden = false;
	};

	const bindSummary = (summaryEl) => {
		if (!summaryEl || summaryEl.dataset.hlReviewBound === "true") return;

		const triggerEl = summaryEl.querySelector("[data-hl-review-trigger]");
		const ctaEl = summaryEl.querySelector("[data-hl-review-scroll]");
		if (!triggerEl || !ctaEl) return;

		const handlePointerEnter = () => {
			if (!getHoverEnabled()) return;
			openSummary(summaryEl);
		};

		const handlePointerLeave = () => {
			if (!getHoverEnabled()) return;
			closeSummary(summaryEl);
		};

		const handleFocusIn = () => openSummary(summaryEl);
		const handleFocusOut = (event) => {
			if (summaryEl.contains(event.relatedTarget)) return;
			closeSummary(summaryEl);
		};

		const handleTriggerClick = () => {
			if (getHoverEnabled()) return;
			if (summaryEl.classList.contains("is-open")) {
				closeSummary(summaryEl);
			} else {
				openSummary(summaryEl);
			}
		};

		const handleEscape = (event) => {
			if (event.key !== "Escape") return;
			closeSummary(summaryEl);
			triggerEl.focus();
		};

		const handleCtaClick = (event) => {
			event.preventDefault();
			closeSummary(summaryEl);
			scrollToReviewWidget(summaryEl);
		};

		summaryEl.addEventListener("mouseenter", handlePointerEnter);
		summaryEl.addEventListener("mouseleave", handlePointerLeave);
		summaryEl.addEventListener("focusin", handleFocusIn);
		summaryEl.addEventListener("focusout", handleFocusOut);
		summaryEl.addEventListener("keydown", handleEscape);
		triggerEl.addEventListener("click", handleTriggerClick);
		ctaEl.addEventListener("click", handleCtaClick);

		summaryEl.dataset.hlReviewBound = "true";
	};

	const observeReviewWidget = (summaryEl) => {
		if (!summaryEl || summaryEl._hlReviewWidgetObserver) return;

		const widgetEl = getReviewWidget(summaryEl.dataset.productId);
		if (!widgetEl) return;

		const observer = new MutationObserver(() => {
			updateSummary(summaryEl);
		});

		observer.observe(widgetEl, {
			childList: true,
			subtree: true,
			characterData: true,
			attributes: true,
		});

		summaryEl._hlReviewWidgetObserver = observer;
	};

	const initReviewSummary = (root = document) => {
		const scope = root instanceof Element ? root : document;
		const summaries = scope.matches && scope.matches(SUMMARY_SELECTOR) ? [scope] : scope.querySelectorAll(SUMMARY_SELECTOR);

		summaries.forEach((summaryEl) => {
			bindSummary(summaryEl);
			updateSummary(summaryEl);
			observeReviewWidget(summaryEl);

			let attempts = 0;
			const retry = () => {
				const hasData = updateSummary(summaryEl);
				observeReviewWidget(summaryEl);
				attempts += 1;
				if (hasData) {
					return;
				}
				if (attempts < 20) {
					window.setTimeout(retry, 400);
				}
			};

			window.setTimeout(retry, 250);
			window.setTimeout(() => updateSummary(summaryEl), 1200);
		});
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => {
			initReviewSummary();
		});
	} else {
		initReviewSummary();
	}

	window.addEventListener("load", () => {
		initReviewSummary();
	});

	document.addEventListener("shopify:section:load", (event) => {
		initReviewSummary(event.target);
	});

	document.addEventListener("click", (event) => {
		const openSummaryEl = document.querySelector(`${SUMMARY_SELECTOR}.is-open`);
		if (!openSummaryEl) return;
		if (openSummaryEl.contains(event.target)) return;
		closeSummary(openSummaryEl);
	});
})();
/* ===== PDP Review Summary: Judge.me Hover Panel + Scroll Link (END) ===== */

/* ===== Countdown Clock: Timezone-Aware Ticker (START) ===== */
/*
 * Notes:
 * - Reads "YYYY-MM-DD HH:MM" end dates as wall-clock time in the chosen timezone.
 * - One shared 1s interval updates every countdown on the page.
 * - Hides the clock or swaps in the ended message once it reaches zero.
 */
(() => {
	const COUNTDOWN_SELECTOR = "[data-hl-countdown]";

	// Offset (ms) between UTC and the given timezone at a UTC instant.
	const getTimezoneOffset = (utcMs, timeZone) => {
		const parts = new Intl.DateTimeFormat("en-US", {
			timeZone,
			hourCycle: "h23",
			year: "numeric",
			month: "2-digit",
			day: "2-digit",
			hour: "2-digit",
			minute: "2-digit",
			second: "2-digit",
		}).formatToParts(new Date(utcMs));
		const get = (type) => Number(parts.find((part) => part.type === type).value);
		const asUtc = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
		return asUtc - utcMs;
	};

	const parseEndTime = (value, timeZone) => {
		const match = String(value || "").trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?$/);
		if (!match) return null;

		const [, year, month, day, hour = "23", minute = "59"] = match;
		const wallClockMs = Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute));

		try {
			// Two passes so the offset is correct across DST changes.
			let utcMs = wallClockMs - getTimezoneOffset(wallClockMs, timeZone);
			utcMs = wallClockMs - getTimezoneOffset(utcMs, timeZone);
			return utcMs;
		} catch (error) {
			return wallClockMs;
		}
	};

	const pad = (value) => String(value).padStart(2, "0");

	const setText = (el, value) => {
		if (el && el.textContent !== value) el.textContent = value;
	};

	const updateCountdown = (countdownEl) => {
		if (countdownEl._hlEndTime === undefined) {
			countdownEl._hlEndTime = parseEndTime(countdownEl.dataset.endDate, countdownEl.dataset.timezone || "America/New_York");
		}

		const endTime = countdownEl._hlEndTime;
		if (endTime === null) {
			countdownEl.hidden = true;
			return;
		}

		const remaining = endTime - Date.now();
		const runningEl = countdownEl.querySelector("[data-hl-countdown-running]");
		const expiredEl = countdownEl.querySelector("[data-hl-countdown-expired]");

		if (remaining <= 0) {
			const hide = countdownEl.dataset.expiredBehavior === "hide";
			countdownEl.hidden = hide;
			if (runningEl) runningEl.hidden = true;
			if (expiredEl) expiredEl.hidden = hide;
			countdownEl.dataset.hlCountdownDone = "true";
			if (countdownEl.matches(STICKY_SELECTOR)) updateStickyHeight();
			return;
		}

		const totalSeconds = Math.floor(remaining / 1000);
		setText(countdownEl.querySelector("[data-hl-countdown-days]"), pad(Math.floor(totalSeconds / 86400)));
		setText(countdownEl.querySelector("[data-hl-countdown-hours]"), pad(Math.floor((totalSeconds % 86400) / 3600)));
		setText(countdownEl.querySelector("[data-hl-countdown-minutes]"), pad(Math.floor((totalSeconds % 3600) / 60)));
		setText(countdownEl.querySelector("[data-hl-countdown-seconds]"), pad(totalSeconds % 60));

		if (runningEl) runningEl.hidden = false;
		if (expiredEl) expiredEl.hidden = true;
		countdownEl.hidden = false;
	};

	const tick = () => {
		document.querySelectorAll(COUNTDOWN_SELECTOR).forEach((countdownEl) => {
			if (countdownEl.dataset.hlCountdownDone === "true") return;
			updateCountdown(countdownEl);
		});
	};

	// Sticky clocks live on <body> so no transformed/overflow ancestor can trap the fixed bar.
	const STICKY_SELECTOR = "[data-hl-countdown-sticky]";
	let stickyObserver = null;

	const updateStickyHeight = () => {
		const stickyEls = Array.from(document.querySelectorAll(`body > ${STICKY_SELECTOR}`)).filter((el) => !el.hidden);
		const height = stickyEls.reduce((max, el) => Math.max(max, el.offsetHeight), 0);
		document.documentElement.style.setProperty("--hl-countdown-sticky-height", `${height}px`);
	};

	const removeStickyForSection = (sectionId) => {
		if (!sectionId) return;
		document.querySelectorAll(`body > ${STICKY_SELECTOR}[data-hl-countdown-section="${sectionId}"]`).forEach((el) => {
			if (stickyObserver) stickyObserver.unobserve(el);
			el.remove();
		});
		updateStickyHeight();
	};

	const mountSticky = (scope = document) => {
		scope.querySelectorAll(STICKY_SELECTOR).forEach((stickyEl) => {
			if (stickyEl.parentElement === document.body) return;
			removeStickyForSection(stickyEl.dataset.hlCountdownSection);
			document.body.appendChild(stickyEl);
			if ("ResizeObserver" in window) {
				if (!stickyObserver) stickyObserver = new ResizeObserver(updateStickyHeight);
				stickyObserver.observe(stickyEl);
			}
		});
		updateStickyHeight();
	};

	// "Show only after scrolling": reveal once the visitor has scrolled past the announcement bar.
	const getScrollThreshold = () => {
		const announcementEl = document.querySelector(".bstro-announcement-bar");
		return Math.max(1, announcementEl ? announcementEl.offsetHeight : 0);
	};

	const updateStickyScroll = () => {
		const threshold = getScrollThreshold();
		document.querySelectorAll(`body > ${STICKY_SELECTOR}`).forEach((stickyEl) => {
			const waitForScroll = stickyEl.hasAttribute("data-hl-countdown-show-on-scroll");
			stickyEl.classList.toggle("is-scroll-hidden", waitForScroll && window.scrollY < threshold);
		});
	};

	let scrollTicking = false;
	window.addEventListener(
		"scroll",
		() => {
			if (scrollTicking) return;
			scrollTicking = true;
			window.requestAnimationFrame(() => {
				updateStickyScroll();
				scrollTicking = false;
			});
		},
		{ passive: true },
	);

	let intervalId = null;
	const start = () => {
		mountSticky();
		updateStickyScroll();
		tick();
		updateStickyHeight();
		if (!intervalId) intervalId = window.setInterval(tick, 1000);
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", start);
	} else {
		start();
	}

	document.addEventListener("shopify:section:load", (event) => {
		removeStickyForSection(event.detail && event.detail.sectionId);
		mountSticky(event.target);
		updateStickyScroll();
		tick();
		updateStickyHeight();
	});
	document.addEventListener("shopify:section:unload", (event) => {
		removeStickyForSection(event.detail && event.detail.sectionId);
	});
	document.addEventListener("shopify:block:select", tick);
})();
/* ===== Countdown Clock: Timezone-Aware Ticker (END) ===== */

/* ===== Announcement Marquee: Auto-Fill Copies + Constant Speed (START) ===== */
/*
 * Notes:
 * - Fills each scrolling text track with just enough copies to cover the bar, so short text never leaves a gap.
 * - Sets the loop duration from the track width so every block scrolls at the same px/s speed.
 * - Re-measures on resize, font load, and when a hidden slide becomes visible.
 */
(() => {
	const TRACK_SELECTOR = ".bstro-announcement-bar [data-hl-marquee-track]";
	const ITEM_CLASS = "announcement-marquee__item";
	const CLONE_CLASS = "announcement-marquee__item--clone";
	const DEFAULT_SPEED = 20;

	const fillTrack = (trackEl) => {
		const viewportEl = trackEl.parentElement;
		const templateEl = trackEl._hlMarqueeTemplate;
		if (!viewportEl || !templateEl) return;

		const firstItem = trackEl.querySelector(`.${ITEM_CLASS}`);
		const viewportWidth = viewportEl.getBoundingClientRect().width;
		const itemWidth = firstItem ? firstItem.getBoundingClientRect().width : 0;
		// Hidden slides measure 0; the ResizeObserver retries once they're shown.
		if (!viewportWidth || !itemWidth) return;

		// Each half of the track must be at least as wide as the bar; +1 covers partial widths.
		const copies = Math.max(1, Math.ceil(viewportWidth / itemWidth) + 1);

		if (trackEl._hlMarqueeCopies !== copies) {
			const fragment = document.createDocumentFragment();
			for (let half = 0; half < 2; half += 1) {
				for (let i = 0; i < copies; i += 1) {
					const itemEl = templateEl.cloneNode(true);
					if (half === 1) {
						itemEl.classList.add(CLONE_CLASS);
						itemEl.setAttribute("aria-hidden", "true");
					}
					fragment.appendChild(itemEl);
				}
			}
			trackEl.replaceChildren(fragment);
			trackEl._hlMarqueeCopies = copies;
		}

		const barEl = trackEl.closest("[data-hl-marquee-speed]");
		const speed = Number.parseFloat(barEl && barEl.dataset.hlMarqueeSpeed) || DEFAULT_SPEED;
		const duration = (copies * itemWidth) / speed;
		trackEl.style.setProperty("--bstro-announcement-marquee-speed", `${duration.toFixed(2)}s`);
	};

	const initTrack = (trackEl) => {
		if (trackEl._hlMarqueeTemplate) {
			fillTrack(trackEl);
			return;
		}

		const sourceItem = trackEl.querySelector(`.${ITEM_CLASS}:not(.${CLONE_CLASS})`);
		if (!sourceItem) return;
		trackEl._hlMarqueeTemplate = sourceItem.cloneNode(true);

		fillTrack(trackEl);

		if ("ResizeObserver" in window) {
			const observer = new ResizeObserver(() => fillTrack(trackEl));
			observer.observe(trackEl.parentElement);
			trackEl._hlMarqueeObserver = observer;
		}
	};

	const initAll = (scope = document) => {
		scope.querySelectorAll(TRACK_SELECTOR).forEach(initTrack);
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", () => initAll());
	} else {
		initAll();
	}

	if (document.fonts && document.fonts.ready) {
		document.fonts.ready.then(() => {
			document.querySelectorAll(TRACK_SELECTOR).forEach(fillTrack);
		});
	}

	if (!("ResizeObserver" in window)) {
		window.addEventListener("resize", () => {
			document.querySelectorAll(TRACK_SELECTOR).forEach(fillTrack);
		});
	}

	document.addEventListener("shopify:section:load", (event) => initAll(event.target));
	document.addEventListener("shopify:section:unload", (event) => {
		event.target.querySelectorAll(TRACK_SELECTOR).forEach((trackEl) => {
			if (trackEl._hlMarqueeObserver) trackEl._hlMarqueeObserver.disconnect();
		});
	});
})();
/* ===== Announcement Marquee: Auto-Fill Copies + Constant Speed (END) ===== */

window.zESettings = {
	webWidget: {
		offset: {
			vertical: "0px" /* Increase this number to move it higher */,
			horizontal: "0px" /* Distance from the side of the screen */,
		},
	},
};
